import React, { useEffect, useState } from 'react';
import styles from './PostCard.module.css';
import PostBody from './PostBody';
import { attachmentDisplayName } from '../../utils/attachments';

async function triggerDownload(url, preferredName) {
  const filename = preferredName || 'download';
  try {
    // cache: 'no-store' — an <img> renders this same URL and caches an opaque
    // (non-CORS) copy; reusing it would make this fetch fail CORS and fall back
    // to opening a tab instead of downloading. See PostDetail for the full note.
    const res = await fetch(url, { mode: 'cors', cache: 'no-store' });
    if (!res.ok) throw new Error();
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(blobUrl);
  } catch {
    window.open(url, '_blank');
  }
}

const REACTIONS = [
  { key: 'like',        emoji: '👍', label: 'Like' },
  { key: 'acknowledge', emoji: '🧡', label: 'Acknowledge' },
  { key: 'useful',      emoji: '💡', label: 'Useful' },
  { key: 'excited',     emoji: '🚀', label: 'Excited' },
];

function formatFileSize(bytes) {
  if (!bytes || Number.isNaN(bytes)) return null;
  const mb = bytes / (1024 * 1024);
  if (mb >= 1) return `${mb.toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

/* The feed API returns attachment_url/type but no byte size, so read it from the
   response headers. S3 may refuse the preflight — in that case we simply omit the
   size rather than blocking the row. */
function useFileSize(url, enabled) {
  const [size, setSize] = useState(null);
  useEffect(() => {
    if (!url || !enabled) return undefined;
    let cancelled = false;
    fetch(url, { method: 'HEAD' })
      .then((res) => {
        const len = res.headers.get('content-length');
        if (!cancelled && len) setSize(formatFileSize(Number(len)));
      })
      .catch(() => { /* CORS or offline — size stays hidden */ });
    return () => { cancelled = true; };
  }, [url, enabled]);
  return size;
}

function PdfIcon() {
  return (
    <span className={styles.pdfIcon} aria-hidden="true">
      <svg width="22" height="26" viewBox="0 0 22 26" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M2 2.5A1.5 1.5 0 0 1 3.5 1h9L20 8.5v15a1.5 1.5 0 0 1-1.5 1.5h-15A1.5 1.5 0 0 1 2 23.5v-21Z" fill="#fff" stroke="#E5484D" strokeWidth="1.6" strokeLinejoin="round"/>
        <path d="M12.5 1 20 8.5h-7.5V1Z" fill="#FDE0E1" stroke="#E5484D" strokeWidth="1.6" strokeLinejoin="round"/>
        <rect x="4.5" y="13" width="13" height="8" rx="1.5" fill="#E5484D"/>
        <text x="11" y="19.2" textAnchor="middle" fontSize="5.2" fontWeight="700" fill="#fff" fontFamily="Segoe UI, Arial, sans-serif">PDF</text>
      </svg>
    </span>
  );
}

function DownloadIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  );
}

/* Solid white padlock, 16:23 per the Figma. Keyhole is punched out to the
   button's orange so it reads as a cut-out. Keep the fills in sync with
   .gatedUpgradeBtn's background. */
function LockIcon() {
  return (
    <svg width="12" height="17" viewBox="0 0 16 23" fill="none" aria-hidden="true" style={{ flexShrink: 0 }}>
      <path d="M4 9.5V5.6a4 4 0 0 1 8 0V9.5" stroke="#ffffff" strokeWidth="2.1" strokeLinecap="round" />
      <rect x="1" y="9.2" width="14" height="12.8" rx="2.6" fill="#ffffff" />
      <circle cx="8" cy="14.6" r="1.6" fill="#f26522" />
      <rect x="7.25" y="15.3" width="1.5" height="3.5" rx="0.75" fill="#f26522" />
    </svg>
  );
}

function PdfAttachment({ post, blurred }) {
  const url = post.attachment_url;
  const size = useFileSize(url, !blurred);
  // Gated cards must not leak the real filename, so they keep the generic label.
  const name = blurred ? 'document.pdf' : attachmentDisplayName(post, 'document.pdf');

  // Gated cards keep the inert row — there is nothing to preview behind the blur.
  if (blurred) {
    return (
      <div className={styles.attachment}>
        <PdfIcon />
        <div className={styles.attachmentInfo}>
          <span className={styles.attachmentName}>{name}</span>
          <span className={styles.attachmentMeta}>PDF{size ? ` • ${size}` : ''}</span>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.attachment}>
      {/* A real anchor rather than a click handler, so middle-click, ctrl+click
          and "open link in new tab" all behave the way people expect. */}
      <a
        className={styles.attachmentOpen}
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
        title={`Preview ${name}`}
      >
        <PdfIcon />
        <div className={styles.attachmentInfo}>
          <span className={styles.attachmentName}>{name}</span>
          <span className={styles.attachmentMeta}>PDF{size ? ` • ${size}` : ''} • Click to preview</span>
        </div>
      </a>
      <button
        className={styles.attachmentDownload}
        onClick={(e) => { e.stopPropagation(); triggerDownload(url, name); }}
        aria-label={`Download ${name}`}
        title="Download"
      >
        <DownloadIcon />
      </button>
    </div>
  );
}

function formatRelativeTime(isoString) {
  const diff = Date.now() - new Date(isoString).getTime();
  const mins  = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days  = Math.floor(diff / 86400000);
  if (mins < 1)  return 'just now';
  if (mins < 60) return `${mins}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7)  return `${days}d ago`;
  return new Date(isoString).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

function AuthorAvatar({ author }) {
  const initial = author.name.charAt(0).toUpperCase();
  const isSuspire = author.type === 'suspire';

  if (isSuspire) {
    return (
      <div className={`${styles.avatar} ${styles.avatarSuspire}`}>
        <img
          src="/images/Suspire_Logo.png"
          alt="Suspire"
          style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }}
        />
      </div>
    );
  }

  if (author.logo_url) {
    return (
      <div className={`${styles.avatar} ${styles.avatarBrandLogo}`}>
        <img
          src={author.logo_url}
          alt={author.name}
          style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }}
          onError={e => { e.target.style.display = 'none'; e.target.parentNode.textContent = initial; }}
        />
      </div>
    );
  }

  return (
    <div className={`${styles.avatar} ${styles.avatarBrand}`}>
      {initial}
    </div>
  );
}

function StatusBadge({ status, rejectionReason }) {
  if (status === 'pending') {
    return <span className={styles.statusPending}>⏳ Pending Review</span>;
  }
  if (status === 'rejected') {
    return (
      <div className={styles.rejectedBlock}>
        <span className={styles.statusRejected}>✗ Rejected</span>
        {rejectionReason && (
          <p className={styles.rejectionReason}>{rejectionReason}</p>
        )}
      </div>
    );
  }
  return null;
}

export default function PostCard({ post, postTypes, onPostClick, onReact, isAdmin, onApprove, onReject, onEdit }) {
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [rejectReason, setRejectReason]     = useState('');

  const typeInfo   = postTypes[post.post_type] || { label: post.post_type, color: '#64748b' };
  const totalReactions = Object.values(post.reactions).reduce((a, b) => a + b, 0);
  const isGated    = post.visibility_gated;
  const isPending  = post.status === 'pending';
  const isRejected = post.status === 'rejected';

  const hasImage = post.attachment_url && post.attachment_type === 'IMAGE';
  const hasPdf   = post.attachment_url && post.attachment_type === 'PDF';

  const handleConfirmReject = () => {
    if (onReject) onReject(post.id, rejectReason.trim());
    setShowRejectForm(false);
    setRejectReason('');
  };

  const showGatedOverlay = isGated && !isAdmin;

  return (
    <div className={`${styles.card} ${isPending ? styles.cardPending : ''} ${isGated ? styles.cardGated : ''} ${isRejected ? styles.cardRejected : ''}`}>

      {/* ── Top row: author + badges (always visible) ── */}
      <div className={styles.topRow}>
        <div className={styles.authorRow}>
          <AuthorAvatar author={post.author} />
          <div>
            <span className={styles.authorName}>
              {post.author.name}
              {post.author.type === 'suspire' && (
                <span className={styles.officialBadge}>Official</span>
              )}
            </span>
            <span className={styles.timestamp}>{formatRelativeTime(post.created_at)}</span>
          </div>
        </div>
        <div className={styles.badges}>
          <span
            className={styles.typeBadge}
            style={{ background: typeInfo.color + '18', color: typeInfo.color, borderColor: typeInfo.color + '40' }}
          >
            {typeInfo.label}
          </span>
        </div>
      </div>

      {/* ── Title (always visible) ── */}
      <h2 className={styles.postTitle}>{post.title}</h2>

      {/* ── Gated: blurred content with upgrade overlay ── */}
      {showGatedOverlay ? (
        <div className={styles.gatedWrapper}>
          <div className={styles.gatedBlurred} aria-hidden="true">
            {post.body && (
              <PostBody post={post} className={styles.postBody} htmlClassName={styles.richBody} />
            )}
            {hasImage && (
              <div className={styles.mediaContainer}>
                <img src={post.attachment_url} alt="" className={styles.mediaImage} />
              </div>
            )}
            {hasPdf && <PdfAttachment post={post} blurred />}
            {post.cta_url && (
              <span className={styles.ctaLink}>{post.cta_label || 'Learn More'} →</span>
            )}
            <div className={styles.reactionsRow}>
              {REACTIONS.map(r => (
                <span key={r.key} className={styles.reactionBtn}>
                  <span className={styles.reactionEmoji}>{r.emoji}</span>
                  <span className={styles.reactionCount}>{post.reactions[r.key] || 0}</span>
                </span>
              ))}
            </div>
          </div>
          <div className={styles.gatedOverlay}>
            <button className={styles.gatedUpgradeBtn} onClick={() => onPostClick(post)}>
              <LockIcon />
              Upgrade to View
            </button>
          </div>
        </div>
      ) : (
        <>
          {/* ── Body preview ── */}
          {(isAdmin || (!isPending && !isRejected)) && post.body && (
            <PostBody post={post} className={styles.postBody} htmlClassName={styles.richBody} />
          )}

          {/* ── Media image ── */}
          {hasImage && (
            <div className={styles.mediaContainer}>
              <a
                href={post.attachment_url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                title="Preview image"
              >
                <img
                  src={post.attachment_url}
                  alt="Post media"
                  className={`${styles.mediaImage} ${styles.mediaImageClickable}`}
                />
              </a>
              <button
                className={styles.mediaDownload}
                onClick={() => triggerDownload(post.attachment_url, attachmentDisplayName(post, 'image'))}
                aria-label="Download image"
                title="Download"
              >
                <DownloadIcon />
              </button>
            </div>
          )}

          {/* ── PDF ── */}
          {hasPdf && <PdfAttachment post={post} />}

          {/* ── CTA link (ungated published posts only) ── */}
          {post.cta_url && !isPending && !isRejected && (
            <a
              href={post.cta_url}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.ctaLink}
            >
              {post.cta_label || 'Learn More'} →
            </a>
          )}

          {/* ── CTA link for admin reviewing pending posts ── */}
          {post.cta_url && isAdmin && isPending && (
            <p className={styles.ctaPreview}>
              CTA: <strong>{post.cta_label || 'Learn More'}</strong> → {post.cta_url}
            </p>
          )}

          {/* ── Status badge for brand's own pending/rejected submissions ── */}
          {!isAdmin && (isPending || isRejected) && (
            <StatusBadge status={post.status} rejectionReason={post.rejection_reason} />
          )}

          {/* ── Admin moderation bar (pending posts only) ── */}
          {isAdmin && isPending && onApprove && (
            <div className={styles.moderationBar}>
              <span className={styles.pendingLabel}>⏳ Awaiting Review</span>
              {!showRejectForm ? (
                <div className={styles.moderationActions}>
                  <button className={`${styles.modBtn} ${styles.modEdit}`} onClick={() => onEdit && onEdit(post)}>✏️ Edit</button>
                  <button className={`${styles.modBtn} ${styles.modReject}`} onClick={() => setShowRejectForm(true)}>✗ Reject</button>
                  <button className={`${styles.modBtn} ${styles.modApprove}`} onClick={() => onApprove(post.id)}>✓ Approve</button>
                </div>
              ) : (
                <div className={styles.rejectForm}>
                  <textarea
                    className={styles.rejectTextarea}
                    placeholder="Reason for rejection (shown to the brand)…"
                    value={rejectReason}
                    onChange={e => setRejectReason(e.target.value)}
                    rows={3}
                  />
                  <div className={styles.rejectActions}>
                    <button className={`${styles.modBtn} ${styles.modEdit}`} onClick={() => { setShowRejectForm(false); setRejectReason(''); }}>Cancel</button>
                    <button className={`${styles.modBtn} ${styles.modReject}`} onClick={handleConfirmReject}>Confirm Reject</button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── Reactions (published posts — admins and brands alike; pending/rejected
                 posts are a moderation state, so they get no reaction bar) ── */}
          {!isPending && !isRejected && (
            <div className={styles.reactionsRow}>
              {REACTIONS.map(r => (
                <button
                  key={r.key}
                  className={`${styles.reactionBtn} ${post.user_reaction === r.key ? styles.reactionActive : ''}`}
                  onClick={() => onReact(post.id, r.key)}
                  title={r.label}
                >
                  <span className={styles.reactionEmoji}>{r.emoji}</span>
                  <span className={styles.reactionCount}>{post.reactions[r.key] || 0}</span>
                </button>
              ))}
              {totalReactions > 0 && (
                <span className={styles.totalReactions}>{totalReactions} reaction{totalReactions !== 1 ? 's' : ''}</span>
              )}
            </div>
          )}

          {/* ── Open post button ── */}
          {!isPending && !isRejected && (
            <div className={styles.openBtnRow}>
              <button className={styles.openBtn} onClick={() => onPostClick(post)}>Open Post →</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
